import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import moment from 'moment';
import ActivityFeedFilters from './ActivityFeedFilters';
import useActivityFeedFilters from '../hooks/useActivityFeedFilters';
import { API_BASE_URL } from '../lib/api';

// Configure axios to send cookies
axios.defaults.withCredentials = true;

const ActivityFeed = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // `filter` currently has no UI control to change it (defaults to 'all');
  // kept as a param to fetchLogs()'s query string in case a future filter
  // control is wired up. Pagination state (`page`/`hasMore`) was dead code —
  // `response.data.nextKey` is read but never surfaced in the UI (no
  // "load more" control exists here), so both were removed rather than kept
  // unused (ESLint no-unused-vars cleanup, issue #238).
  const [filter] = useState('all');
  const [refreshing, setRefreshing] = useState(false);

  // Use custom filter hook
  const {
    filteredEvents,
    searchTerm,
    setSearchTerm,
    dateRange,
    setDateRange,
    applyDatePreset,
    selectedEventTypes,
    setSelectedEventTypes,
    clearFilters,
    activeFilterCount
  } = useActivityFeedFilters(logs);

  // Icon mapping for event types
  const getEventIcon = (eventType) => {
    const iconMap = {
      'LOGIN': '🔐',
      'LOGOUT': '👋',
      'HOLDING_CREATED': '✅',
      'HOLDING_UPDATED': '✏️',
      'HOLDING_DELETED': '🗑️',
      'HOLDING_SOLD': '💸',
      'PORTFOLIO_REFRESHED': '🔄',
      'SNAPSHOT_CREATED': '📸',
      'SNAPSHOT_DELETED': '🗑️',
      'CONTRIBUTION_ADDED': '💰',
      'CONTRIBUTION_UPDATED': '✏️',
      'CONTRIBUTION_DELETED': '🗑️',
      'WITHDRAWAL_ADDED': '💵',
      'PRICE_UPDATE': '💹',
      'ERROR_OCCURRED': '❌'
    };
    return iconMap[eventType] || '📋';
  };

  // Get severity color
  const getSeverityColor = (severity) => {
    const colorMap = {
      'info': '#3B82F6',
      'warning': '#F59E0B',
      'error': '#EF4444',
      'success': '#10B981'
    };
    return colorMap[severity] || '#6B7280';
  };

  // Fetch logs from API
  const fetchLogs = useCallback(async (reset = false) => {
    try {
      const isRefresh = !reset && logs.length > 0;
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      const params = new URLSearchParams({
        limit: 20,
        ...(filter !== 'all' && { eventType: filter })
      });

      const response = await axios.get(`${API_BASE_URL}/logs?${params.toString()}`);

      if (response.data && response.data.logs) {
        setLogs(response.data.logs);
      }
    } catch (err) {
      console.error('Error fetching logs:', err);
      setError(err.response?.data?.error || 'Failed to load activity feed');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [filter, logs.length]);

  // Initial fetch. `fetchLogs` is intentionally excluded from the deps array:
  // it is recreated whenever `logs.length` changes (see its own useCallback
  // deps above), so including it here would refetch on every successful
  // fetch, an infinite loop. `filter` is the only value that should trigger
  // a fresh initial fetch.
  useEffect(() => {
    fetchLogs(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchLogs(false);
    }, 30000);

    return () => clearInterval(interval);
  }, [fetchLogs]);

  // Format event description
  const formatDescription = (log) => {
    if (log.description) return log.description;

    // Fallback descriptions based on event type
    const descriptions = {
      'LOGIN': 'User logged in',
      'LOGOUT': 'User logged out',
      'HOLDING_CREATED': `Created holding${log.metadata?.assetName ? `: ${log.metadata.assetName}` : ''}`,
      'HOLDING_UPDATED': `Updated holding${log.metadata?.assetName ? `: ${log.metadata.assetName}` : ''}`,
      'HOLDING_DELETED': `Deleted holding${log.metadata?.assetName ? `: ${log.metadata.assetName}` : ''}`,
      'HOLDING_SOLD': `Sold ${log.metadata?.units || ''} units${log.metadata?.assetName ? ` of ${log.metadata.assetName}` : ''}`,
      'PORTFOLIO_REFRESHED': 'Portfolio prices refreshed',
      'SNAPSHOT_CREATED': 'Portfolio snapshot created',
      'SNAPSHOT_DELETED': 'Snapshot deleted',
      'CONTRIBUTION_ADDED': `Added contribution${log.metadata?.amount ? `: ${log.metadata.amount} ${log.metadata.currency}` : ''}`,
      'CONTRIBUTION_UPDATED': 'Updated contribution',
      'CONTRIBUTION_DELETED': 'Deleted contribution',
      'WITHDRAWAL_ADDED': `Added withdrawal${log.metadata?.amount ? `: ${log.metadata.amount} ${log.metadata.currency}` : ''}`,
      'PRICE_UPDATE': 'Portfolio prices updated',
      'ERROR_OCCURRED': `Error: ${log.metadata?.error?.message || 'An error occurred'}`
    };

    return descriptions[log.eventType] || log.eventType.replace(/_/g, ' ');
  };

  return (
    <div className="activity-feed-container" style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <h2 style={{ margin: 0, color: 'var(--color-text-primary)' }}>Activity Feed</h2>
          {refreshing && (
            <div className="live-indicator" style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '12px',
              color: '#10B981',
              padding: '4px 8px',
              backgroundColor: 'rgba(16, 185, 129, 0.1)',
              borderRadius: '12px'
            }}>
              <div style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: '#10B981',
                animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
              }}></div>
              Updating...
            </div>
          )}
        </div>
        <button
          onClick={() => fetchLogs(true)}
          disabled={loading || refreshing}
          style={{
            padding: '8px 16px',
            borderRadius: '6px',
            border: '1px solid var(--color-border)',
            backgroundColor: 'var(--color-bg-secondary)',
            color: 'var(--color-text-primary)',
            cursor: loading || refreshing ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            opacity: loading || refreshing ? 0.6 : 1
          }}
        >
          🔄 Refresh
        </button>
      </div>

      {/* Advanced Filters */}
      <ActivityFeedFilters
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        dateRange={dateRange}
        setDateRange={setDateRange}
        applyDatePreset={applyDatePreset}
        selectedEventTypes={selectedEventTypes}
        setSelectedEventTypes={setSelectedEventTypes}
        clearFilters={clearFilters}
        activeFilterCount={activeFilterCount}
        totalEvents={logs.length}
        filteredCount={filteredEvents.length}
      />

      {error && (
        <div style={{
          padding: '12px 16px',
          marginBottom: '16px',
          borderRadius: '6px',
          backgroundColor: '#FEE2E2',
          color: '#991B1B',
          border: '1px solid #FCA5A5'
        }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ padding: '20px' }}>
          <Skeleton count={5} height={60} style={{ marginBottom: '12px' }} />
        </div>
      ) : filteredEvents.length === 0 ? (
        <div style={{
          padding: '40px 20px',
          textAlign: 'center',
          color: 'var(--color-text-secondary)',
          backgroundColor: 'var(--color-bg-secondary)',
          borderRadius: '8px',
          border: '1px dashed var(--color-border)'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '12px' }}>📋</div>
          <div style={{ fontSize: '16px', fontWeight: '500', marginBottom: '8px' }}>
            {logs.length === 0 ? 'No activity yet' : 'No matching events'}
          </div>
          <div style={{ fontSize: '14px' }}>
            {logs.length === 0 ? 'Your recent actions will appear here' : 'Try adjusting your filters'}
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {filteredEvents.map((log) => (
            <div
              key={log.logId}
              className="activity-item"
              style={{
                padding: '16px',
                backgroundColor: 'var(--color-bg-secondary)',
                borderRadius: '8px',
                border: `1px solid var(--color-border)`,
                borderLeft: `4px solid ${getSeverityColor(log.severity)}`,
                display: 'flex',
                gap: '12px',
                alignItems: 'flex-start',
                transition: 'all 0.2s',
                cursor: 'default'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--color-bg-hover)';
                e.currentTarget.style.transform = 'translateX(4px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
                e.currentTarget.style.transform = 'translateX(0)';
              }}
            >
              <div style={{ fontSize: '24px', flexShrink: 0 }}>
                {getEventIcon(log.eventType)}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px' }}>
                  <span className="event-type" style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: 'var(--color-text-primary)'
                  }}>
                    {formatDescription(log)}
                  </span>
                  <span className="event-time" style={{
                    fontSize: '12px',
                    color: 'var(--color-text-secondary)',
                    flexShrink: 0,
                    marginLeft: '12px'
                  }}>
                    {moment(log.timestamp).fromNow()}
                  </span>
                </div>
                {log.metadata && Object.keys(log.metadata).length > 0 && (
                  <div className="event-description" style={{
                    fontSize: '13px',
                    color: 'var(--color-text-secondary)',
                    marginTop: '6px'
                  }}>
                    {log.metadata.assetName && <div>Asset: {log.metadata.assetName}</div>}
                    {log.metadata.ticker && <div>Ticker: {log.metadata.ticker}</div>}
                    {log.metadata.units && <div>Units: {log.metadata.units}</div>}
                    {log.metadata.amount && <div>Amount: {log.metadata.amount} {log.metadata.currency || ''}</div>}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }
      `}</style>
    </div>
  );
};

export default ActivityFeed;
